import { useState } from 'react';
import { ReconTransactionClient, ReconTransactionDTO, ApiResponse_string, ApiResponse_reconTransactionDTO } from '../services/apiClient';
import { API_BASE_URL } from '../config/config';
import axiosInstance from '../services/axiosInstance';

export const useReconTransactionApi = () => {
    const apiClient = new ReconTransactionClient(API_BASE_URL, axiosInstance);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const create = async (data: ReconTransactionDTO) => {
        setIsLoading(true);
        try {
            await apiClient.createReconTransaction(data);
        } catch (err: any) {
            setError(err.message || 'Error creating ReconTransaction');
        } finally {
            setIsLoading(false);
        }
    };

    const edit = async (id: string, data: ReconTransactionDTO) => {
        setIsLoading(true);
        try {
            await apiClient.updateReconTransaction(id, data);
        } catch (err: any) {
            setError(err.message || 'Error updating ReconTransaction');
        } finally {
            setIsLoading(false);
        }
    };

    const getById = async (id: string) => {
        setIsLoading(true);
        try {
            return await apiClient.getReconTransactionById(id);
        } catch (err: any) {
            setError(err.message || 'Error fetching ReconTransaction');
        } finally {
            setIsLoading(false);
        }
    };

    const search = async (filters: any) => {
        setIsLoading(true);
        try {
            return await apiClient.searchReconTransactions(filters);
        } catch (err: any) {
            setError(err.message || 'Error searching ReconTransactions');
        } finally {
            setIsLoading(false);
        }
    };

    const remove = async (id: string) => {
        setIsLoading(true);
        try {
            await apiClient.deleteReconTransaction(id);
        } catch (err: any) {
            setError(err.message || 'Error deleting ReconTransaction');
        } finally {
            setIsLoading(false);
        }
    };

    const setManuallyIgnored = async (id: string): Promise<ApiResponse_reconTransactionDTO> => {
        setIsLoading(true);
        try {
            return await apiClient.setReconTransactionManuallyIgnored(id);
        } catch (err: any) {
            setError(err.message || 'Error ignoring ReconTransaction');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const extract = async (
        filters: any
    ): Promise<ApiResponse_string> => {
        setIsLoading(true);
        try {
            return await apiClient.exportReconTransactions(filters);
        } catch (err: unknown) {
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const setIsUnallocated = async (id: string, isUnallocated: boolean): Promise<ApiResponse_reconTransactionDTO> => {
        setIsLoading(true);
        try {
            const response = await apiClient.setReconTransactionIsUnallocated(id, { isUnallocated });
            return response;
        } catch (err: any) {
            setError(err.message || 'Error updating ReconTransaction allocation');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const setMemberId = async (id: string, memberId?: string): Promise<ApiResponse_reconTransactionDTO> => {
        setIsLoading(true);
        try {
            const body = memberId ? { memberId } : {};
            const response = await apiClient.setReconTransactionMember(id, body);
            return response;
        } catch (err: any) {
            setError(err.message || 'Error updating ReconTransaction member');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const setComponentId = async (id: string, componentId?: string): Promise<ApiResponse_reconTransactionDTO> => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.post(`/recontransaction/${id}/set-component`,
                componentId ? { componentId } : {},
            );
            return response.data as ApiResponse_reconTransactionDTO;
        } catch (err: any) {
            setError(err.message || 'Error updating ReconTransaction component');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    return { create, edit, getById, search, extract, remove, setManuallyIgnored, setIsUnallocated, setMemberId, setComponentId, isLoading, error };
};